import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import type { EmployeeTimeEntry } from "@/lib/employeeTimes";
import {
  getRecentAuftraege,
  isBookableForHours,
  searchAuftraege,
  type AuftragLookup,
} from "@/lib/material";
import { formatAuftragNumberWithYear } from "@/lib/orderDraftUtils";

/** Platzhalter wie Materialanforderung / Projektablage. */
export const AUFTRAG_AUTOCOMPLETE_PLACEHOLDER =
  "Klicken für zuletzt benutzte – oder Nummer / Kunde / Ort tippen …";

export const AUFTRAG_AUTOCOMPLETE_EMPTY =
  "Noch keine Aufträge benutzt – tippe eine Nummer, einen Kunden oder einen Ort.";

/** Zwei Zeilen: Projektnummer oben, Beschreibung · Kunde · Ort unten (wie Autocomplete). */
export function auftragLookupToOption(
  row: AuftragLookup,
): AutocompleteOption<AuftragLookup> {
  const label = formatAuftragNumberWithYear(row) || row.beschreibung || "Projekt";
  const descriptionParts =
    label === row.beschreibung
      ? [row.kunde, row.ort_zusatz || row.ort]
      : [row.beschreibung, row.kunde, row.ort_zusatz || row.ort];
  return {
    key: String(row.id || row.auftragsnummer || label),
    label,
    description: descriptionParts
      .filter((v): v is string => !!v && String(v).trim() !== "")
      .join(" · "),
    value: row,
  };
}

export type LoadAuftragAutocompleteOptions = {
  /** Für Zeiterfassung: fertiggestellte / abgerechnete / bezahlte Projekte ausblenden. */
  forHourBooking?: boolean;
  /** Berechtigung für nachträgliche Stunden relativ zu diesem Login prüfen. */
  forUserLogin?: string | null;
};

/**
 * Leeres Feld → zuletzt benutzte Aufträge (`/me/recent-auftraege`).
 * Mit Eingabe → Volltextsuche wie Material / Bestellungen.
 */
export async function loadAuftragAutocompleteOptions(
  query: string,
  options: LoadAuftragAutocompleteOptions = {},
): Promise<AutocompleteOption<AuftragLookup>[]> {
  const forHourBooking = Boolean(options.forHourBooking);
  const forUserLogin = options.forUserLogin || undefined;
  const trimmed = query.trim();
  let list: AuftragLookup[];
  if (!trimmed) {
    list = await getRecentAuftraege({
      limit: 25,
      includeCompleted: forHourBooking ? false : undefined,
      forUserLogin,
    }).catch(() => [] as AuftragLookup[]);
  } else {
    list = await searchAuftraege(trimmed, { forUserLogin }).catch(() => [] as AuftragLookup[]);
  }
  if (forHourBooking) {
    list = list.filter(isBookableForHours);
  }
  return list.map(auftragLookupToOption);
}

function parseAuftragsnummerAndYear(
  raw: string,
  jahrHint?: number | null,
): { auftragsnummer: string; jahr: number | null } {
  const trimmed = raw.trim();
  if (jahrHint != null && Number.isFinite(Number(jahrHint))) {
    const year = Number(jahrHint);
    const suffix = `-${year}`;
    // Falls die Nummer schon `…-2026` enthält, Jahr nicht doppelt anhängen.
    if (trimmed.endsWith(suffix) && trimmed.length > suffix.length) {
      return { auftragsnummer: trimmed.slice(0, -suffix.length), jahr: year };
    }
    return { auftragsnummer: trimmed, jahr: year };
  }
  // Legacy: API lieferte früher schon `nummer-jahr` ohne separates jahr.
  const dash = trimmed.lastIndexOf("-");
  if (dash > 0) {
    const maybeYear = Number(trimmed.slice(dash + 1));
    if (Number.isFinite(maybeYear) && maybeYear >= 2000 && maybeYear <= 2100) {
      return { auftragsnummer: trimmed.slice(0, dash), jahr: maybeYear };
    }
  }
  return { auftragsnummer: trimmed, jahr: null };
}

/** Anzeige-Nummer wie vor dem Speichern (`2-8#1-2026`). */
export function formatEmployeeTimeProjectNumber(entry: {
  auftragsnummer?: string | null;
  jahr?: number | null;
}): string {
  const raw = String(entry.auftragsnummer || "").trim();
  if (!raw) return "";
  const { auftragsnummer, jahr } = parseAuftragsnummerAndYear(raw, entry.jahr);
  return formatAuftragNumberWithYear({ auftragsnummer, jahr }) || raw;
}

export function employeeTimeEntryToAuftragLookup(
  entry: EmployeeTimeEntry,
): AuftragLookup | null {
  const hasLocalAuftrag = entry.auftrag_id != null && entry.auftrag_id > 0;
  const raw = String(entry.auftragsnummer || (hasLocalAuftrag ? entry.auftrag_id : "") || "").trim();
  const hasLabel = Boolean(raw || entry.beschreibung || entry.kunde);
  if (!hasLocalAuftrag && !hasLabel) return null;

  const { auftragsnummer, jahr } = parseAuftragsnummerAndYear(raw, entry.jahr);

  return {
    // Ohne lokales Mapping: Anzeige-only (kein Positions-Select).
    id: hasLocalAuftrag ? Number(entry.auftrag_id) : 0,
    auftragsnummer: auftragsnummer || (entry.beschreibung ? "Hero" : ""),
    jahr,
    kunde: entry.kunde ?? null,
    ort: entry.ort ?? null,
    ort_zusatz: entry.ort_zusatz ?? null,
    beschreibung: entry.beschreibung ?? null,
    workflow_status: entry.workflow_status ?? null,
    project_completed: entry.project_completed ?? null,
    late_hour_permission: entry.late_hour_permission ?? null,
  };
}

/** Zeiten auf fertiggestellten / abgerechneten / bezahlten Projekten sind gesperrt. */
export function isEmployeeTimeEntryLocked(entry: EmployeeTimeEntry): boolean {
  return !isBookableForHours(entry);
}

export function employeeTimeEntryToProjectOption(
  entry: EmployeeTimeEntry,
): AutocompleteOption<AuftragLookup> | null {
  const lookup = employeeTimeEntryToAuftragLookup(entry);
  return lookup ? auftragLookupToOption(lookup) : null;
}
